/**
 * Wizard step calculation from live datasource state and backend wizard_session.
 */

export const WIZARD_STORAGE_KEY = "sellyx-brain-wizard-progress";

const IN_FLIGHT_STATUSES = new Set(["processing", "indexing"]);

export function clearProgress(sourceId) {
  try {
    const raw = localStorage.getItem(WIZARD_STORAGE_KEY);
    if (!raw) return;
    if (!sourceId) {
      localStorage.removeItem(WIZARD_STORAGE_KEY);
      return;
    }
    const data = JSON.parse(raw);
    if (data.sourceId === sourceId) {
      localStorage.removeItem(WIZARD_STORAGE_KEY);
    }
  } catch {
    localStorage.removeItem(WIZARD_STORAGE_KEY);
  }
}

function syncIsOk(status) {
  return status === "completed" || status === "skipped";
}

export function getWizardSession(detail) {
  return detail?.wizard_session ?? detail?.metadata?.wizard_session ?? null;
}

export function hasRecoverableSetupProgress(detail) {
  if (!detail) return false;
  const docs = Number(detail.document_count) || 0;
  const chunks = Number(detail.chunk_count) || 0;
  if (docs > 0 || chunks > 0) return true;
  if (IN_FLIGHT_STATUSES.has(detail.status)) return true;
  // Save & Exit on step 2 (no uploads yet) must still be resumable.
  const session = getWizardSession(detail);
  return Boolean(session?.saved) && !session?.completed;
}

/**
 * Derive wizard step from backend session and datasource fields.
 * Steps: 1 Create → 2 Upload → 3 Process → 4 Success.
 * (Agent assignment was removed in V1 — sources auto-assign to all agents.)
 * @returns {1|2|3|4}
 */
export function computeWizardStep(detail) {
  if (!detail) return 1;

  const docs = Number(detail.document_count) || 0;
  const chunks = Number(detail.chunk_count) || 0;
  const session = getWizardSession(detail);

  // Completed setups (step 4 success, or legacy step 5 from pre-V1 wizard).
  if (session?.completed && docs > 0 && chunks > 0) return 4;
  if (session?.step === 2 && docs === 0 && (session?.saved || session?.force_upload)) return 2;

  if (docs === 0) return 2;

  if (chunks === 0 || detail.status === "processing" || detail.status === "uploading" || detail.status === "indexing") {
    return 3;
  }

  if (!syncIsOk(detail.openclaw_sync_status || "pending")) {
    return 3;
  }

  // Indexed + synced but wizard not marked complete — finish on Process.
  return 3;
}

export function isSetupComplete(detail) {
  if (!detail) return false;

  const docs = Number(detail.document_count) || 0;
  const chunks = Number(detail.chunk_count) || 0;
  if (docs === 0 || chunks === 0) return false;
  if (!syncIsOk(detail.openclaw_sync_status || "pending")) return false;

  const session = getWizardSession(detail);
  if (session?.completed) return true;
  // Legacy success screen before V1 step renumbering.
  if (session?.step === 5) return true;

  // Legacy sources (predating wizard_session) with indexed knowledge.
  return !session;
}

export function needsSetupContinuation(source) {
  if (!source) return true;
  // Saved drafts (incl. step 1 only) and in-flight / indexed incomplete setups.
  if (!hasRecoverableSetupProgress(source)) return false;
  if (isSetupComplete(source)) return false;
  return computeWizardStep(source) < 4;
}

export function logWizardState(event, detail, calculatedStep) {
  if (process.env.NODE_ENV !== "development") return;
  // eslint-disable-next-line no-console
  console.info(`[brain-wizard] ${event}`, {
    sourceId: detail?.id,
    documentCount: detail?.document_count ?? 0,
    chunkCount: detail?.chunk_count ?? 0,
    syncStatus: detail?.openclaw_sync_status,
    status: detail?.status,
    assignments: detail?.assignments,
    wizardSession: getWizardSession(detail),
    hasProgress: hasRecoverableSetupProgress(detail),
    calculatedStep,
  });
}
